import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { aggregateFanOutQueryState } from '~/components/PriceParrot/PriceParrotChainLpcLppRatioDashboard/utils';

import PriceParrotService from '~/services/PriceParrotService';

import type { ChainLpcLppSupplierPropertyGroup, ChainLpcLppSupplierSortMode } from '~/types/services/priceParrot';

import { chainLpcLppSupplierQueryKeys } from './chainLpcLppSupplierQueryKeys';

/**
 * Fans out one breakdown request per selected chain and merges the results —
 * the endpoint only accepts a single chain_name.
 */
export function useGetChainLpcLppSupplierBreakdowns(
  chainNames: ReadonlyArray<string>,
  dateFrom: string | undefined,
  dateTo: string | undefined,
  sort: ChainLpcLppSupplierSortMode,
) {
  const queries = useQueries({
    queries: chainNames.map((chainName) => ({
      queryKey: chainLpcLppSupplierQueryKeys.breakdown(chainName, dateFrom ?? '', dateTo ?? '', sort, null),
      queryFn: async ({ signal }: { signal?: AbortSignal }) => {
        if (!dateFrom || !dateTo) {
          throw new Error('Chain LPC-LPP supplier breakdown requires dateFrom and dateTo');
        }
        const res = await PriceParrotService.getParityTrendChainLpcLppSuppliers(
          chainName,
          { date_from: dateFrom, date_to: dateTo, sort },
          signal,
        );
        return res.data;
      },
      enabled: Boolean(chainName && dateFrom && dateTo),
    })),
  });

  const { isFetching, isError, error } = aggregateFanOutQueryState(chainNames, queries);

  const properties = useMemo<Array<ChainLpcLppSupplierPropertyGroup>>(
    () => queries.flatMap((q) => q.data?.properties ?? []),
    [queries],
  );

  return { properties, isFetching, isError, error };
}
